// src/modals/_PricingManualEditBody.jsx — Phase 6.B PR #167a (ADR-099) + PR #167b (ADR-100)
//
// Body extrait depuis PricingManualEditModal.jsx (refactor pur Option Hybride).
// Composant pur "body" : aucune logique de chrome (overlay), aucun Esc handler,
// aucun click-overlay. Le wrapper (PricingManualEditModal thin wrapper OU
// UnifiedDateActionModal) gère le chrome.
//
// PR #167b (ADR-100) — extension min_stay override par date :
//   - Pré-fill `price` ET `minStay` depuis `pricingMap.get(id)?.get(date)` (D-3 P-b)
//   - Champ `min_stay` form optionnel int 1-30 (D-2 V-b)
//   - Affichage `fetched_at` + bouton "🔄 Recharger" (Q-D2 Option A anti-écrasement)
//   - Payload submit inclut `min_stay` seulement si renseigné (omis sinon)
//
// State interne local (price, minStay, raison, submitting, confirmStage, serverError).
// Reset state sur transition `open: false → true` via useEffect [open].
//
// Préfixe `_` (convention `_block-helpers.js`) = composant interne, ne pas
// importer directement depuis CalendarPanel — passer par UnifiedDateActionModal
// ou le thin wrapper PricingManualEditModal.

import { useEffect, useState } from "react";
import { Coins, Lock, Unlock, AlertTriangle, RefreshCw, CheckCircle, Loader2 } from "lucide-react";
import { DS } from "../lib/ui";
import { countDatesToLock, PRICING_LOCK_LABEL } from "../lib/pricing-display";
import { supabase } from "../supabase";

/**
 * Body extrait Prix manual edit.
 * @param {object} props
 * @param {boolean} props.open — utilisé uniquement pour reset state à l'ouverture
 * @param {string} props.logementId
 * @param {string} [props.logementLabel]
 * @param {string} props.dateFrom — ISO YYYY-MM-DD
 * @param {string} props.dateTo — ISO YYYY-MM-DD
 * @param {boolean} props.dryRunActive
 * @param {Map} [props.pricingMap] — PR #167b : pre-fill price/min_stay actuels + fetched_at
 * @param {function} [props.onReload] — PR #167b : déclenche invalidate parent (anti-écrasement)
 * @param {function} [props.onDirtyChange] — PR #167b amend : notifié(isDirty:bool)
 *                                            à chaque évolution du form (utilisé pour confirmation
 *                                            au switch tab dans UnifiedDateActionModal).
 * @param {function} props.onClose — fermer la modal (bouton Annuler)
 * @param {function} props.onSaved — callback succès, signature ({ ok, ...json })
 */
export default function PricingManualEditBody({
  open,
  logementId,
  logementLabel,
  dateFrom,
  dateTo,
  dryRunActive,
  pricingMap,
  onReload,
  onDirtyChange,
  onClose,
  onSaved,
}) {
  const [price, setPrice] = useState("");
  const [minStay, setMinStay] = useState("");
  const [raison, setRaison] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmStage, setConfirmStage] = useState(0);
  const [serverError, setServerError] = useState(null);
  // WAGON R5 lot F — prompt « prix sous le plancher ». Quand le serveur répond
  // 400 below_floor, on ouvre ce prompt (min_price + price_eur) + champ raison
  // obligatoire → re-submit avec force:true. Pas de pré-check client (la règle
  // vit côté serveur ; on rebondit sur le 400).
  const [floorPrompt, setFloorPrompt] = useState(null); // { min_price, price_eur }
  const [floorReason, setFloorReason] = useState("");
  // LOT 4 — déverrouillage : état dédié (indépendant du submit prix).
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  // LOT 3 — bascule "1 jour" / "période".
  //   - mode "single" : comportement actuel inchangé (date_from=date_to=dateFrom).
  //   - mode "range"  : champs du/au, le prix s'applique à toute la plage.
  const [periodMode, setPeriodMode] = useState("single");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  // PR #167b — extract current day data from pricingMap (référence stable).
  // dayData : { price1, num_avail, min_stay, fetched_at } ou undefined.
  const dayData = pricingMap?.get?.(logementId)?.get?.(dateFrom);
  const initialPrice = dayData?.price1 != null ? String(dayData.price1) : "";
  const initialMinStay =
    dayData?.min_stay != null ? String(dayData.min_stay) : "";
  const fetchedAt = dayData?.fetched_at || null;
  // LOT 4 — la date cliquée est-elle verrouillée (pilotée manuellement) ?
  const isLocked = dayData?.is_manual_override === true;

  // Reset state à chaque ouverture (transition `open: false → true`)
  // PR #167b (D-3 P-b) : pré-remplit price + minStay actuels depuis pricingMap.
  useEffect(() => {
    if (open) {
      setPrice(initialPrice);
      setMinStay(initialMinStay);
      setRaison("");
      setSubmitting(false);
      setConfirmStage(0);
      setServerError(null);
      // WAGON R5 lot F — reset prompt plancher.
      setFloorPrompt(null);
      setFloorReason("");
      // LOT 4 — reset état déverrouillage.
      setUnlocking(false);
      setUnlockError(null);
      // LOT 3 — réinitialise la bascule en mode "1 jour" et pré-remplit
      // les bornes période sur la date cliquée (du = au = dateFrom).
      setPeriodMode("single");
      setRangeFrom(dateFrom);
      setRangeTo(dateFrom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // PR #167b amend — Dirty detection : form considéré dirty dès qu'un champ
  // diverge des initial props OU dès qu'une raison est saisie. Permet au
  // wrapper UnifiedDateActionModal de demander confirmation avant switch tab.
  useEffect(() => {
    if (!onDirtyChange) return;
    const isDirty =
      price !== initialPrice ||
      minStay !== initialMinStay ||
      raison !== "" ||
      periodMode === "range";
    onDirtyChange(isDirty);
  }, [
    price,
    minStay,
    raison,
    periodMode,
    initialPrice,
    initialMinStay,
    onDirtyChange,
  ]);

  // LOT 3 — dates effectives selon la bascule.
  //   single : la date cliquée (dateFrom). range : les bornes du/au saisies.
  const effectiveFrom = periodMode === "range" ? rangeFrom : dateFrom;
  const effectiveTo = periodMode === "range" ? rangeTo : dateFrom;
  // Validation période : bornes non vides + du <= au (comparaison ISO lexicale OK).
  const rangeValid =
    periodMode === "single" ||
    (rangeFrom !== "" && rangeTo !== "" && rangeFrom <= rangeTo);

  const priceNum = Number(price);
  const priceValid =
    price !== "" &&
    Number.isFinite(priceNum) &&
    priceNum > 0 &&
    priceNum < 10000;

  // PR #167b — validation min_stay : vide OK (omit) OU int 1-30.
  const minStayNum = Number(minStay);
  const minStayValid =
    minStay === "" ||
    (Number.isInteger(minStayNum) && minStayNum >= 1 && minStayNum <= 30);

  const formValid = priceValid && minStayValid && rangeValid;

  // WAGON R5 lot F — `override` = { force:true, reason } lors d'une re-soumission
  // après prompt plancher. Absent au 1er envoi (on laisse le serveur trancher).
  async function postEndpoint(override) {
    setSubmitting(true);
    setServerError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setServerError("Non authentifié");
        setSubmitting(false);
        return;
      }
      // PR #167b — payload conditionnel : min_stay inclus seulement si renseigné.
      const body = {
        logement_id: logementId,
        date_from: effectiveFrom,
        date_to: effectiveTo,
        price_eur: priceNum,
        raison: raison || null,
      };
      if (minStay !== "" && minStayValid) {
        body.min_stay = minStayNum;
      }
      // WAGON R5 lot F — override plancher : force + reason (raison obligatoire).
      if (override && override.force === true) {
        body.force = true;
        body.reason = override.reason;
      }
      const res = await fetch("/api/beds24-mapping?action=pricing_set_manual", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        // WAGON R5 lot F — prix sous le plancher : ouvrir le prompt de
        // confirmation (raison obligatoire) au lieu d'afficher une erreur sèche.
        if (json.error === "below_floor") {
          setFloorPrompt({
            min_price: json.min_price,
            price_eur: json.price_eur,
          });
          setServerError(null);
          setSubmitting(false);
          return;
        }
        setServerError(json.message || json.error || `http_${res.status}`);
        setSubmitting(false);
        return;
      }
      onSaved && onSaved({ ok: true, ...json });
    } catch (e) {
      setServerError(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }

  // WAGON R5 lot F — re-soumission forcée après confirmation + raison.
  function handleForceBelowFloor() {
    const r = floorReason.trim();
    if (r === "" || submitting) return;
    setFloorPrompt(null);
    postEndpoint({ force: true, reason: r });
  }

  // WAGON R5 lot F — annulation du prompt plancher : aucun écrit.
  function handleCancelFloor() {
    setFloorPrompt(null);
    setFloorReason("");
  }

  function handlePrimary() {
    if (!formValid || submitting) return;
    if (dryRunActive) {
      // DRY_RUN ON : 1 seule confirmation
      postEndpoint();
    } else if (confirmStage === 0) {
      // DRY_RUN OFF : étape intermédiaire confirmation explicite
      setConfirmStage(1);
    } else {
      postEndpoint();
    }
  }

  // PR #167b — bouton "🔄 Recharger" : invalidate parent + reset form depuis dayData refreshé.
  function handleReload() {
    if (typeof onReload === "function") {
      try {
        onReload();
      } catch (_) {
        /* swallow */
      }
    }
  }

  // LOT 4 — déverrouiller la date / sous-période : repasse is_manual_override
  // à false → la/les date(s) redeviennent AUTO (Beds24) + re-fetch immédiat
  // côté serveur. Réutilise la bascule 1 jour / période (effectiveFrom/To) pour
  // déverrouiller une sous-période. ZÉRO push Beds24.
  async function handleUnlock() {
    if (unlocking || !rangeValid) return;
    setUnlocking(true);
    setUnlockError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setUnlockError("Non authentifié");
        setUnlocking(false);
        return;
      }
      const res = await fetch("/api/beds24-mapping?action=pricing_unlock", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          logement_id: logementId,
          date_from: effectiveFrom,
          date_to: effectiveTo,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUnlockError(json.message || json.error || `http_${res.status}`);
        setUnlocking(false);
        return;
      }
      // Succès : prévenir le parent (refresh calendrier + close) comme pour un save.
      onSaved && onSaved({ ok: true, ...json, action: "unlock" });
    } catch (e) {
      setUnlockError(e.message || String(e));
    } finally {
      setUnlocking(false);
    }
  }

  // ── VERROU SÉLECTIF — annonce du nombre EXACT de dates verrouillées ──
  //
  // CAUSE RACINE (2026-08-03) : un prix posé du 3 au 31 août a verrouillé 69
  // dates en silence. Rien ne l'annonçait. C'est le volet le plus important du
  // lot : les autres ne traitent que les symptômes.
  //
  // Le nombre est EXACT et calculé ici, SANS aller-retour serveur :
  // `pricing_set_manual` verrouille toutes les dates de [date_from, date_to]
  // bornes incluses, sans exception (cf. countDatesToLock + expandRangeToRows).
  //
  // Nuance dite explicitement à l'écran : en DRY_RUN, l'endpoint retourne AVANT
  // tout upsert `daily_pricing` → AUCUNE date n'est verrouillée. Annoncer « 29
  // dates » en dry-run serait un chiffre faux.
  const datesToLock = rangeValid ? countDatesToLock(effectiveFrom, effectiveTo) : null;

  // LOT 3 — libellé reflétant la cible effective (1 jour ou plage saisie).
  const sameDay = effectiveFrom === effectiveTo;
  const periodLabel = sameDay
    ? `Date : ${effectiveFrom}`
    : `Période : du ${effectiveFrom} au ${effectiveTo}`;

  return (
    <div data-testid="pricing-manual-edit-body">
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: DS.fontDisplay,
            fontSize: 18,
            fontWeight: 600,
            color: DS.marineDark,
          }}
        >
          <Coins size={18} strokeWidth={2.2} /> Modifier le prix manuel
        </div>
        <div style={{ fontSize: 12, color: DS.textSecondary, marginTop: 6 }}>
          Logement : <strong>{logementLabel || logementId}</strong>
        </div>
        <div style={{ fontSize: 12, color: DS.textSecondary, display: "flex", alignItems: "center", gap: 5 }}>
          Mode :{" "}
          <span style={{ color: DS.successText, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: DS.success, display: "inline-block" }} /> manual</span>{" "}
          (FBZ source de vérité)
        </div>
        <div style={{ fontSize: 12, color: DS.textSecondary, marginTop: 4 }}>
          {periodLabel}
        </div>
      </div>

      {/* VERROU SÉLECTIF — ANNONCE AVANT VALIDATION (volet principal du lot).
          Remplace l'ancienne mention discrète « le prix manuel verrouille la
          date » : (a) elle ne disait pas COMBIEN de dates, (b) elle est devenue
          fausse — le verrou ne fige plus que le PRIX. Rendue en bloc plein,
          au-dessus du formulaire, avec le compte exact. Jetons DS existants
          (warningBg / warning / warningText), aucune couleur nouvelle. */}
      <div
        data-testid="pricing-lock-announce"
        style={{
          marginBottom: 14,
          padding: 10,
          background: DS.warningBg,
          border: `1px solid ${DS.warning}`,
          borderRadius: 8,
          fontSize: 12,
          color: DS.warningText,
        }}
      >
        <div
          data-testid="pricing-lock-announce-count"
          style={{ display: "flex", alignItems: "flex-start", gap: 6, fontWeight: 700, marginBottom: 6 }}
        >
          <Lock size={13} strokeWidth={2.3} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            {dryRunActive
              ? "Aucune date ne sera verrouillée : le mode DRY_RUN n'écrit pas le prix en base."
              : datesToLock == null
                ? "Période invalide : corrigez les dates pour connaître le nombre de dates verrouillées."
                : datesToLock === 1
                  ? "1 date sera verrouillée par cet enregistrement."
                  : `${datesToLock} dates seront verrouillées par cet enregistrement.`}
          </span>
        </div>
        <div style={{ lineHeight: 1.45 }}>
          Sur une date verrouillée, <strong>seul le prix</strong> est figé : il
          n'est plus repris de Beds24 et le calendrier l'affiche «&nbsp;{PRICING_LOCK_LABEL}&nbsp;».
          La disponibilité et les nuits minimum, elles, continuent de se
          synchroniser normalement. Le déverrouillage se fait date par date ou
          par période, depuis cette même fenêtre.
        </div>
      </div>

      {/* LOT 4 — badge + bouton déverrouiller si la date est verrouillée. */}
      {isLocked && (
        <div
          data-testid="pricing-locked-banner"
          style={{
            marginBottom: 12,
            padding: 10,
            background: DS.warningBg,
            border: `1px solid ${DS.warning}`,
            borderRadius: 8,
          }}
        >
          <div
            data-testid="pricing-locked-badge"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              fontWeight: 700,
              color: DS.warningText,
              marginBottom: 6,
            }}
          >
            <Lock size={13} strokeWidth={2.3} /> Verrouillée — {PRICING_LOCK_LABEL}
          </div>
          {/* VERROU SÉLECTIF — texte remis à l'état RÉEL (règle « textes d'aide
              = état actuel ») : le cron réécrit bien la dispo et les nuits
              minimum de cette date ; c'est le PRIX seul qu'il ne touche pas. */}
          <div style={{ fontSize: 11, color: DS.warningText, marginBottom: 8 }}>
            Le cron Beds24 ne réécrit pas le <strong>prix</strong> de cette date
            (la disponibilité et les nuits minimum, elles, restent synchronisées).
            Déverrouiller rend le prix automatique (re-suivi Beds24) et recharge
            la valeur réelle tout de suite. Utilisez la bascule 1&nbsp;jour /
            Période ci-dessous pour déverrouiller une sous-période.
          </div>
          <button
            type="button"
            className="fbz-press"
            onClick={handleUnlock}
            disabled={unlocking || !rangeValid}
            data-testid="pricing-unlock-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: DS.card,
              color: DS.warningText,
              border: `1px solid ${DS.warning}`,
              borderRadius: DS.radiusControl,
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: unlocking || !rangeValid ? "not-allowed" : "pointer",
              opacity: unlocking || !rangeValid ? 0.6 : 1,
            }}
          >
            {unlocking
              ? <><Loader2 size={13} /> Déverrouillage…</>
              : <><Unlock size={13} strokeWidth={2.3} /> Déverrouiller / re-suivre Beds24</>}
          </button>
          {unlockError && (
            <div
              data-testid="pricing-unlock-error"
              style={{ display: "flex", alignItems: "center", gap: 5, color: DS.dangerText, fontSize: 11, marginTop: 6 }}
            >
              <AlertTriangle size={12} strokeWidth={2.2} /> {unlockError}
            </div>
          )}
        </div>
      )}

      {/* LOT 3 — bascule "1 jour" / "période". */}
      <div style={{ marginBottom: 12 }}>
        <div
          role="group"
          aria-label="Portée du prix"
          style={{
            display: "inline-flex",
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setPeriodMode("single");
              setConfirmStage(0);
            }}
            disabled={submitting}
            data-testid="pricing-mode-single"
            aria-pressed={periodMode === "single"}
            style={{
              background: periodMode === "single" ? DS.navy : "#fff",
              color: periodMode === "single" ? "#fff" : DS.navy,
              border: "none",
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            1 jour
          </button>
          <button
            type="button"
            onClick={() => {
              setPeriodMode("range");
              setConfirmStage(0);
            }}
            disabled={submitting}
            data-testid="pricing-mode-range"
            aria-pressed={periodMode === "range"}
            style={{
              background: periodMode === "range" ? DS.navy : "#fff",
              color: periodMode === "range" ? "#fff" : DS.navy,
              border: "none",
              borderLeft: `0.5px solid ${DS.borderHover}`,
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            Période
          </button>
        </div>
      </div>

      {periodMode === "range" && (
        <div
          style={{ display: "flex", gap: 10, marginBottom: 12 }}
          data-testid="pricing-range-fields"
        >
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 700,
                color: DS.navy,
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              Du *
            </label>
            <input
              type="date"
              value={rangeFrom}
              onChange={(e) => {
                setRangeFrom(e.target.value);
                setConfirmStage(0);
              }}
              disabled={submitting}
              data-testid="pricing-range-from"
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 6,
                border: `0.5px solid ${DS.borderHover}`,
                fontSize: 14,
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 700,
                color: DS.navy,
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              Au *
            </label>
            <input
              type="date"
              value={rangeTo}
              min={rangeFrom || undefined}
              onChange={(e) => {
                setRangeTo(e.target.value);
                setConfirmStage(0);
              }}
              disabled={submitting}
              data-testid="pricing-range-to"
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 6,
                border: `0.5px solid ${DS.borderHover}`,
                fontSize: 14,
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>
      )}
      {periodMode === "range" && !rangeValid && (
        <div
          style={{ color: DS.red, fontSize: 11, marginTop: -6, marginBottom: 10 }}
          data-testid="pricing-range-error"
        >
          La date de fin doit être postérieure ou égale à la date de début.
        </div>
      )}

      <div style={{ marginBottom: 10 }}>
        <label
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 700,
            color: DS.navy,
            marginBottom: 4,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Prix EUR *
        </label>
        <input
          type="number"
          min="1"
          max="9999"
          step="1"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="ex: 120"
          disabled={submitting}
          data-testid="pricing-price-input"
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 6,
            border: `0.5px solid ${DS.borderHover}`,
            fontSize: 14,
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
        />
        {price !== "" && !priceValid && (
          <div style={{ color: DS.red, fontSize: 11, marginTop: 3 }}>
            Prix doit être un nombre entre 1 et 9999.
          </div>
        )}
        {/* PR #167b (Q-D2 Option A) — affichage fetched_at + bouton recharger.
            Anti-écrasement silencieux : si le prix a changé Beds24 entre mount
            et submit (cron 6h OU drift PriceLabs), user peut recharger avant push. */}
        {fetchedAt && (
          <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 3 }}>
            Prix actuel chargé :{" "}
            {new Date(fetchedAt).toLocaleString("fr-FR", {
              dateStyle: "short",
              timeStyle: "short",
            })}
            {typeof onReload === "function" && (
              <>
                {" • "}
                <button
                  type="button"
                  onClick={handleReload}
                  disabled={submitting}
                  data-testid="pricing-reload-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    background: "none",
                    border: "none",
                    color: DS.marine,
                    cursor: submitting ? "not-allowed" : "pointer",
                    fontSize: 10,
                    textDecoration: "underline",
                    padding: 0,
                    fontFamily: "inherit",
                  }}
                >
                  <RefreshCw size={10} strokeWidth={2.2} /> Recharger
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* PR #167b — champ min_stay (override par date, D-2 V-b). */}
      <div style={{ marginBottom: 14 }}>
        <label
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 700,
            color: DS.navy,
            marginBottom: 4,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Séjour minimum (override)
        </label>
        <input
          type="number"
          min={1}
          max={30}
          step={1}
          value={minStay}
          onChange={(e) => setMinStay(e.target.value)}
          placeholder="Vide = pas d'override"
          disabled={submitting}
          data-testid="pricing-min-stay-input"
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 6,
            border: `0.5px solid ${DS.borderHover}`,
            fontSize: 14,
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
        />
        {minStay !== "" && !minStayValid && (
          <div style={{ color: DS.red, fontSize: 11, marginTop: 3 }}>
            min_stay doit être un entier entre 1 et 30.
          </div>
        )}
        <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 3 }}>
          Override cette date uniquement. Le réglage par défaut par logement
          arrive dans une prochaine mise à jour.
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 700,
            color: DS.navy,
            marginBottom: 4,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Raison (facultatif)
        </label>
        <input
          type="text"
          value={raison}
          onChange={(e) => setRaison(e.target.value)}
          placeholder="ex: ajustement pic été"
          disabled={submitting}
          maxLength={120}
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 6,
            border: `0.5px solid ${DS.borderHover}`,
            fontSize: 13,
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
        />
      </div>

      {dryRunActive ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            padding: 10,
            background: DS.successBg,
            color: DS.successText,
            border: `1px solid ${DS.success}55`,
            borderRadius: 8,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <CheckCircle size={14} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>Mode DRY_RUN ACTIF : aucun push réel Beds24 ne sera effectué.
          L'action sera tracée dans <code>pricing_audit_log</code>.</span>
        </div>
      ) : confirmStage === 0 ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            padding: 10,
            background: DS.dangerBg,
            color: DS.dangerText,
            border: `1px solid ${DS.danger}44`,
            borderRadius: 8,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <AlertTriangle size={14} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>DRY_RUN OFF : un push <strong>RÉEL</strong> Beds24 sera fait. Le
          prix sera modifié sur toutes les OTAs marketplace.</span>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            padding: 10,
            background: DS.warningBg,
            color: DS.warningText,
            border: `1px solid ${DS.warning}`,
            borderRadius: 8,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {/* VERROU SÉLECTIF — le compte est REDIT au dernier écran avant le
              push réel : c'est la seule étape que Dimitri lit vraiment. */}
          <AlertTriangle size={14} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span><strong>Confirmation finale</strong> : êtes-vous sûr ? Cette
          action modifie le prix sur les OTAs
          {datesToLock != null && (
            <> et verrouille <strong data-testid="pricing-lock-announce-confirm">{datesToLock}</strong>{" "}
            {datesToLock === 1 ? "date" : "dates"}</>
          )}.</span>
        </div>
      )}

      {/* WAGON R5 lot F — prompt « prix sous le plancher » (override confirmé). */}
      {floorPrompt && (
        <div
          data-testid="pricing-floor-prompt"
          style={{
            padding: 12,
            background: DS.warningBg,
            color: DS.warningText,
            border: `1px solid ${DS.warning}`,
            borderRadius: 8,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 700, marginBottom: 6 }}>
            <AlertTriangle size={14} strokeWidth={2.3} /> Prix {floorPrompt.price_eur} € sous le plancher{" "}
            {floorPrompt.min_price} € — confirmer ?
          </div>
          <div style={{ marginBottom: 8 }}>
            Pour passer outre le prix plancher, indiquez une raison (obligatoire).
          </div>
          <input
            type="text"
            value={floorReason}
            onChange={(e) => setFloorReason(e.target.value)}
            placeholder="Raison du passage sous le plancher"
            disabled={submitting}
            maxLength={200}
            data-testid="pricing-floor-reason"
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 6,
              border: "1px solid #fbbf24",
              fontSize: 13,
              fontFamily: "inherit",
              boxSizing: "border-box",
              marginBottom: 8,
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              type="button"
              className="fbz-press"
              onClick={handleCancelFloor}
              disabled={submitting}
              data-testid="pricing-floor-cancel"
              style={{
                background: DS.card,
                color: DS.warningText,
                border: `1px solid ${DS.warning}`,
                borderRadius: DS.radiusControl,
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "inherit",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              Annuler
            </button>
            <button
              type="button"
              className="fbz-press"
              onClick={handleForceBelowFloor}
              disabled={submitting || floorReason.trim() === ""}
              data-testid="pricing-floor-confirm"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background:
                  submitting || floorReason.trim() === ""
                    ? DS.borderHover
                    : DS.corail,
                color: "#fff",
                border: "none",
                borderRadius: DS.radiusControl,
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "inherit",
                cursor:
                  submitting || floorReason.trim() === ""
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {submitting ? <><Loader2 size={13} /> Envoi…</> : "Confirmer quand même"}
            </button>
          </div>
        </div>
      )}

      {serverError && (
        <div
          style={{
            display: "flex",
            gap: 7,
            alignItems: "flex-start",
            padding: 8,
            background: DS.dangerBg,
            color: DS.dangerText,
            borderRadius: 6,
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          <AlertTriangle size={13} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>{serverError}</span>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          className="fbz-press"
          onClick={onClose}
          disabled={submitting}
          style={{
            background: DS.card,
            color: DS.marine,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: submitting ? "not-allowed" : "pointer",
          }}
        >
          Annuler
        </button>
        <button
          type="button"
          className="fbz-press"
          onClick={handlePrimary}
          disabled={!formValid || submitting || floorPrompt != null}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background:
              !dryRunActive && confirmStage === 1 ? DS.danger : DS.corail,
            color: "#fff",
            border: "none",
            borderRadius: DS.radiusControl,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 700,
            fontFamily: "inherit",
            cursor:
              !formValid || submitting || floorPrompt != null
                ? "not-allowed"
                : "pointer",
            opacity: !formValid || submitting || floorPrompt != null ? 0.5 : 1,
          }}
        >
          {submitting
            ? <><Loader2 size={13} /> Envoi…</>
            : dryRunActive
              ? "Confirmer (dry_run)"
              : confirmStage === 0
                ? "Confirmer"
                : "Oui, pousser le prix"}
        </button>
      </div>
    </div>
  );
}
